import { LitElement, html, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '../../../components/hover-card/agwa-hover-card';
import operationTypeChipsStyles from './operation-type-chips.css?inline';

/** One chip: the short code it carries, and the words the code stands for. */
export interface OperationTypeChip {
  code: string;
  label: string;
}

/**
 * <operation-type-chips class="type-chips">
 *   <sl-tag slot="chip">PSS</sl-tag> ...
 *   <div slot="legend">PSS - Prime System Supplier</div> ...
 * </operation-type-chips>
 *
 * A row's operation types as short-code chips, whose legend (every code with its words) opens over
 * them on hover, keyboard focus or a tap. Drawn the same way by the company list and the staff list,
 * so a type reads identically on both. Hosts write it through renderOperationTypeChips.
 *
 * THE LEGEND IS DRAWN IN THE TOP LAYER (U33 item 7). It was an `sl-tooltip hoist`, and hoisting
 * only escapes clipping: the popup stayed inside the row's stacking context (agwa-lit-table gives
 * every row segment one), so on the first row it opened upwards under the table's sticky header and
 * could not be read, the way the person card was painted under later rows before U40 F1. It now
 * opens through <agwa-hover-card>, the primitive that fixed the person card. The anchor is a button,
 * so a keyboard or touch user can open the legend too; a mouse click still reaches the clickable row.
 *
 * AN ELEMENT, SO ITS LOOK TRAVELS WITH IT. The rows are drawn inside agwa-lit-table's shadow root,
 * where a host page's stylesheet does not reach, so the button reset and the card's look live in
 * this element's own shadow styles. The chips and legend lines stay the host's light children,
 * slotted in: the row's text and its legend's words stay where the tooltip left them, readable from
 * the row whether the legend is open or not.
 */
@customElement('operation-type-chips')
export class OperationTypeChips extends LitElement {
  static styles = [unsafeCSS(operationTypeChipsStyles)];

  render() {
    return html`<agwa-hover-card
      ><button slot="anchor" type="button" class="chips">
        <slot name="chip"></slot>
      </button>
      <div class="legend"><slot name="legend"></slot></div
    ></agwa-hover-card>`;
  }
}

/**
 * The chips and legend for a row's operation types, in the order given. The host has already
 * chosen which types show and resolved each code to its short code and words.
 *
 * The chips are Shoelace's neutral, not a status colour: there is no semantic status-colour standard
 * yet (D-040), and an operation type is a classification rather than a state, so a colour would
 * invent a meaning the palette has not defined. (Carried over from the company list's stylesheet,
 * whose chip rules could not reach inside the table and are gone.)
 */
export function renderOperationTypeChips(chips: readonly OperationTypeChip[]) {
  return html`<operation-type-chips class="type-chips"
    >${chips.map(
      (chip) =>
        html`<sl-tag slot="chip" size="small" variant="neutral"
          >${chip.code}</sl-tag
        >`
    )}${chips.map(
      (chip) => html`<div slot="legend">${chip.code} - ${chip.label}</div>`
    )}</operation-type-chips
  >`;
}

declare global {
  interface HTMLElementTagNameMap {
    'operation-type-chips': OperationTypeChips;
  }
}
